import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, AlertTriangle } from 'lucide-react';
import PolicyList from '../../components/PolicyList';
import { AUTH_CONFIG } from '../../config';
import AdminLayout from '../../components/AdminLayout';

const PoliciesAdmin = () => {
  const [activeStatus, setActiveStatus] = useState('all');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  
  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Use the shared AUTH_CONFIG helper instead of direct fetch
        const isAuth = await AUTH_CONFIG.isAuthenticated();
        setIsAuthenticated(isAuth);
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthChecking(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // Handle status tab change
  const handleStatusChange = (status) => {
    setActiveStatus(status);
  };
  
  // Status tabs
  const statusTabs = [
    { id: 'all', name: 'All Policies' },
    { id: 'draft', name: 'Drafts' },
    { id: 'analyzed', name: 'Analyzed' },
    { id: 'live', name: 'Published' }
  ];
  
  // Content to render when authenticated
  const authContent = (
    <div className="container mx-auto px-4 max-w-6xl">
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Policy Management
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage government policy analysis with Claude AI
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/admin/policies/add"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add New Policy
          </Link>
        </div>
      </div>
      
      {/* Status tabs */}
      <div className="bg-white overflow-hidden shadow rounded-lg mb-8">
        <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {statusTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleStatusChange(tab.id)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${activeStatus === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>
        <div className="bg-gray-50 px-4 py-6 sm:p-6">
          <PolicyList status={activeStatus} isAdmin={true} />
        </div>
      </div>
    </div>
  );

  // Loading spinner while checking auth
  const loadingContent = (
    <div className="text-center py-8">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      <p className="mt-2 text-gray-600">Checking authentication...</p>
    </div>
  );

  // Not authenticated message
  const unauthContent = (
    <div className="py-8 bg-red-50 rounded-lg text-center">
      <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
      <h2 className="text-xl font-bold text-red-800 mb-2">Authentication Required</h2>
      <p className="text-red-600 mb-6">
        You must be logged in as an administrator to access this area.
      </p>
      <Link
        to="/admin"
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
      >
        Go to Admin Login
      </Link>
    </div>
  );

  return (
    <AdminLayout>
      {authChecking ? loadingContent : !isAuthenticated ? unauthContent : authContent}
    </AdminLayout>
  );
};

export default PoliciesAdmin;